import type {
  Event,
  SpanBeginEvent,
  SpanEndEvent,
  StepEvent,
} from "@tsmono/inspect-common/types";

import { hasSpans } from "./utils";

// It will be caught elsewhere and rendered with a pretty name
export const kSandboxSignalName = "53787D8A-D3FC-426D-B383-9F880B70E4AA";

/**
 * Normalizes event content
 */
export const fixupEventStream = (
  events: Event[],
  filterPending: boolean = true
) => {
  // We ignore pending events sometimes (when an eval is complete) and
  // show them other times (when an eval is running)
  const collapsed = processPendingEvents(events, filterPending);

  // Collapse the per-span-level echoes of a single store mutation
  const deduped = dedupeStoreEventEchoes(collapsed);

  // We need to inject a step event for sample_init if it doesn't exist
  const fixedUp = collapseSampleInit(deduped);

  // Inject step events before and after groups of sandbox events
  return groupSandboxEvents(fixedUp);
};

// inspect_ai snapshots the store at every span begin and emits a StoreEvent
// diff at every span end, so a mutation inside nested spans is re-reported
// once per enclosing level — identical changes separated only by span_end /
// state events (the span-close cascade). Drop each such echo in favor of the
// next one, keeping only the outermost (last, least-indented) report. Store
// events separated by any substantive event are genuine repeat reports and
// are kept.
const dedupeStoreEventEchoes = (events: Event[]): Event[] => {
  const echoTransparent = (e: Event) =>
    e.event === "span_end" || e.event === "state";

  return events.filter((event, i) => {
    if (event.event !== "store") {
      return true;
    }
    let j = i + 1;
    while (j < events.length && echoTransparent(events[j]!)) {
      j++;
    }
    const next = events[j];
    return !(
      next?.event === "store" &&
      JSON.stringify(next.changes) === JSON.stringify(event.changes)
    );
  });
};

const processPendingEvents = (events: Event[], filter: boolean): Event[] => {
  // Coalesce repeated emissions of the *same* logical event (same uuid)
  // while it remains pending — that's the streaming-update case. Distinct
  // uuids (e.g. parallel sibling tool calls) survive as independent rows.
  // Events with uuid=null (synthetic step/span events) never coalesce.
  return filter
    ? events.filter((e) => !e.pending)
    : events.reduce<Event[]>((acc, event) => {
        if (!event.pending) {
          acc.push(event);
          return acc;
        }
        const lastIndex = acc.length - 1;
        if (
          lastIndex >= 0 &&
          event.uuid != null &&
          acc[lastIndex]?.pending &&
          acc[lastIndex].uuid === event.uuid
        ) {
          acc[lastIndex] = event;
        } else {
          acc.push(event);
        }
        return acc;
      }, []);
};

const collapseSampleInit = (events: Event[]): Event[] => {
  // Don't performance sample init logic if spans are present
  const hasSpans = events.some((e) => {
    return e.event === "span_begin" || e.event === "span_end";
  });
  if (hasSpans) {
    return events;
  }

  // Don't synthesize a sample init step if one already exists
  const hasInitStep =
    events.findIndex((e) => {
      return e.event === "step" && e.name === "init";
    }) !== -1;
  if (hasInitStep) {
    return events;
  }

  // Find a sample init event
  const initEventIndex = events.findIndex((e) => {
    return e.event === "sample_init";
  });
  const initEvent = events[initEventIndex];
  if (!initEvent) {
    return events;
  }

  // Splice in sample init step if needed
  const fixedUp = [...events];
  fixedUp.splice(initEventIndex, 0, {
    timestamp: initEvent.timestamp,
    event: "step",
    action: "begin",
    type: null,
    name: "sample_init",
    pending: false,
    working_start: 0,
    span_id: initEvent.span_id,
    uuid: null,
    metadata: null,
  });

  fixedUp.splice(initEventIndex + 2, 0, {
    timestamp: initEvent.timestamp,
    event: "step",
    action: "end",
    type: null,
    name: "sample_init",
    pending: false,
    working_start: 0,
    span_id: initEvent.span_id,
    uuid: null,
    metadata: null,
  });
  return fixedUp;
};

const groupSandboxEvents = (events: Event[]): Event[] => {
  const result: Event[] = [];
  const pendingSandboxEvents: Event[] = [];

  const useSpans = hasSpans(events);

  const pushPendingSandboxEvents = () => {
    const timestamp =
      pendingSandboxEvents[pendingSandboxEvents.length - 1]?.timestamp || "";
    if (useSpans) {
      result.push(createSpanBegin(kSandboxSignalName, timestamp, null));
    } else {
      result.push(createStepEvent(kSandboxSignalName, timestamp, "begin"));
    }
    result.push(...pendingSandboxEvents);
    if (useSpans) {
      result.push(createSpanEnd(kSandboxSignalName, timestamp));
    } else {
      result.push(createStepEvent(kSandboxSignalName, timestamp, "end"));
    }
    pendingSandboxEvents.length = 0;
  };

  for (const event of events) {
    if (event.event === "sandbox") {
      // Collect sandbox events
      pendingSandboxEvents.push(event);
      continue;
    }

    // Process any collected sandbox events
    if (pendingSandboxEvents.length > 0) {
      pushPendingSandboxEvents();
    }

    // Clear sandbox events and add the current event
    result.push(event);
  }

  // Handle any remaining sandbox events at the end
  if (pendingSandboxEvents.length > 0) {
    pushPendingSandboxEvents();
  }

  return result;
};

const createStepEvent = (
  name: string,
  timestamp: string,
  action: "begin" | "end"
): StepEvent => ({
  timestamp,
  event: "step",
  action,
  type: null,
  name,
  pending: false,
  working_start: 0,
  span_id: null,
  uuid: null,
  metadata: null,
});

const createSpanBegin = (
  name: string,
  timestamp: string,
  parent_id: string | null
): SpanBeginEvent => {
  return {
    name,
    id: `${name}-begin`,
    span_id: name,
    parent_id,
    timestamp,
    event: "span_begin",
    type: null,
    pending: false,
    working_start: 0,
    uuid: null,
    metadata: null,
  };
};

const createSpanEnd = (name: string, timestamp: string): SpanEndEvent => {
  return {
    id: `${name}-end`,
    timestamp,
    event: "span_end",
    pending: false,
    working_start: 0,
    span_id: name,
    uuid: null,
    metadata: null,
  };
};
